/**
 * Tiny state module for "new service worker version is available".
 *
 * vite-plugin-pwa fires `onNeedRefresh` when a fresh build's SW has installed
 * and is ready to take over. The running tab is still on old JS in memory, so
 * the user needs to reload to fully pick up the new version. We surface that
 * as an amber state on the header `StatusLight` — clicking the dot calls the
 * plugin's returned `updateSW(true)` to reload.
 */

import { useSyncExternalStore } from 'react';

let needRefresh = false;
let updateFn: ((reloadPage?: boolean) => Promise<void>) | null = null;
const listeners = new Set<() => void>();

function emit(): void {
  for (const l of listeners) l();
}

/** Called by the SW registration hook when a new version is waiting. */
export function registerSwUpdate(
  update: (reloadPage?: boolean) => Promise<void>,
): void {
  updateFn = update;
  needRefresh = true;
  emit();
}

export function useSwUpdate(): {
  needRefresh: boolean;
  applyUpdate: () => void;
} {
  const value = useSyncExternalStore(
    (cb) => {
      listeners.add(cb);
      return () => listeners.delete(cb);
    },
    () => needRefresh,
    () => false,
  );
  return {
    needRefresh: value,
    applyUpdate: () => {
      if (updateFn) void updateFn(true);
      else window.location.reload();
    },
  };
}
